import { formatDateNumber, formatDateTime } from '@seen/core/dates'

import { cn, TypographyPUI } from '@seen/ui'
import {
  format as dateFnsFormat,
  formatDistance,
  isBefore,
  isValid,
  parse,
  parseISO,
} from 'date-fns'
import { type JSX, useMemo } from 'react'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'

export interface HoverDateProps {
  date: string | Date
  format?: 'relative' | 'date' | 'datetime'
  tooltipPrefix?: string
  // Extra line rendered below the formatted date in the tooltip, e.g. a
  // secondary date such as when a backdated record was actually authored.
  tooltipDetail?: string
}

/*
 * Returns true when `date` is a date-only ISO string (e.g. "2024-03-06"),
 * meaning it has no time component and should be interpreted in local time,
 * not UTC. parseISO() treats bare date strings as midnight UTC, which shows
 * the wrong day in US timezones during evening hours.
 */
function isDateOnly(date: string | Date): boolean {
  return typeof date === 'string' && !date.includes('T') && date.length <= 10
}

/*
 * Converts a date value to a JS Date. Date-only strings are parsed in local
 * time via the explicit format pattern to avoid the UTC-midnight off-by-one
 * day issue. Datetime strings and Date objects use parseISO / passthrough.
 */
function toDate(date: string | Date): Date {
  if (typeof date === 'string') {
    if (isDateOnly(date)) {
      return parse(date, 'yyyy-MM-dd', new Date())
    }
    return parseISO(date)
  }
  return date
}

export function HoverDate({
  date,
  format = 'date',
  tooltipPrefix,
  tooltipDetail,
}: HoverDateProps): JSX.Element {
  const today = useMemo(() => new Date(), [])
  const parsed = useMemo(() => toDate(date), [date])

  if (!isValid(parsed)) {
    return <span className="text-foreground-tertiary">—</span>
  }

  const tooltipFormat =
    format === 'date' || isDateOnly(date)
      ? 'EEEE, MMMM d, yyyy'
      : "EEEE, MMMM d, yyyy 'at' h:mm a"

  return (
    <Tooltip>
      <TooltipTrigger
        className={cn(
          'text-nowrap decoration-1 underline-offset-[2.5px] hover:underline'
        )}
        tabIndex={-1}
      >
        {format === 'relative' &&
          (isBefore(parsed, today)
            ? `${formatDistance(parsed, today)} ago`
            : `in ${formatDistance(today, parsed)}`)}
        {format === 'date' && formatDateNumber(parsed)}
        {format === 'datetime' && formatDateTime(parsed)}
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="-ml-3">
        <TypographyPUI className="font-normal">
          {tooltipPrefix && `${tooltipPrefix} `}
          {dateFnsFormat(parsed, tooltipFormat)}
        </TypographyPUI>
        {tooltipDetail && (
          <TypographyPUI className="font-normal text-muted-foreground">
            {tooltipDetail}
          </TypographyPUI>
        )}
      </TooltipContent>
    </Tooltip>
  )
}
